import {
  ConfirmationModal,
  IconAttention,
  Space,
  Text,
  useModal,
  WarningAlert,
} from '@procurify/ui'
import { useHistory } from 'react-router'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { useMutateDeactivateLegalEntity } from '@webapp/platform/legal-entities/features/deactivate-legal-entity/models'
import messages from './messages'

interface DeactivateLegalEntityInput {
  uuid: string
  name: string
  locationCount: number
  /** Whether disabling stops invoices arriving at the entity's inbox. */
  hasInboxEmailAddress: boolean
}

/**
 * Drives the "Disable entity" action from both the list row and the details
 * page. Which modal opens is decided client-side from `locationCount` (per
 * Figma): with locations still mapped, deactivation is blocked and the modal
 * links to Location settings instead of confirming. The backend re-validates
 * both blocked cases (locations mapped / last active entity) on DELETE, so a
 * stale count degrades to the error toast rather than a bad deactivation.
 */
export const useDeactivateLegalEntityPresenter = () => {
  const { deactivate, isLoading } = useMutateDeactivateLegalEntity()
  const intl = useProcurifyIntl()
  const modal = useModal()
  const history = useHistory()
  const { url } = useAppRoutes()

  const performDeactivate = async ({
    uuid,
    name,
  }: Pick<DeactivateLegalEntityInput, 'uuid' | 'name'>) => {
    try {
      await deactivate({ uuid })

      toast.success({
        title: intl.formatMessage(messages.successToastTitle, { name }),
      })
    } catch (error) {
      const errorMessage =
        getMessageFromError(error) ||
        intl.formatMessage(messages.defaultErrorMessage)

      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children: errorMessage,
      })
    }
  }

  const showBlockedModal = ({
    name,
    locationCount,
  }: Pick<DeactivateLegalEntityInput, 'name' | 'locationCount'>) => {
    modal.showModal(ConfirmationModal, {
      open: true,
      icon: <IconAttention />,
      type: 'warning',
      header: intl.formatMessage(messages.blockedModalTitle),
      content: intl.formatMessage(messages.blockedModalBody, {
        count: locationCount,
        name,
      }),
      confirmButtonText: intl.formatMessage(messages.blockedModalConfirmButton),
      cancelButtonText: intl.formatMessage(messages.cancelButton),
      handleDismiss: () => {
        modal.hideModal()
      },
      onConfirm: () => {
        modal.hideModal()
        history.push(url('PrimaryCategoryConfigListPage', { tab: 'active' }))
      },
    })
  }

  const showConfirmModal = ({
    uuid,
    name,
    hasInboxEmailAddress,
  }: Omit<DeactivateLegalEntityInput, 'locationCount'>) => {
    const body = intl.formatMessage(messages.confirmModalBody)

    modal.showModal(ConfirmationModal, {
      open: true,
      icon: <IconAttention />,
      type: 'warning',
      header: intl.formatMessage(messages.confirmModalTitle, { name }),
      content: hasInboxEmailAddress ? (
        <Space>
          <Text>{body}</Text>
          <WarningAlert>
            {intl.formatMessage(messages.confirmModalInboxWarning)}
          </WarningAlert>
        </Space>
      ) : (
        body
      ),
      confirmButtonText: intl.formatMessage(messages.confirmModalConfirmButton),
      cancelButtonText: intl.formatMessage(messages.cancelButton),
      handleDismiss: () => {
        modal.hideModal()
      },
      // `performDeactivate` never throws (errors end in the error toast), so
      // the modal always closes once the request settles. ConfirmationForm's
      // own isSubmitting state disables the button while awaiting.
      onConfirm: async () => {
        await performDeactivate({ uuid, name })
        modal.hideModal()
      },
    })
  }

  const deactivateLegalEntity = ({
    uuid,
    name,
    locationCount,
    hasInboxEmailAddress,
  }: DeactivateLegalEntityInput) => {
    if (locationCount > 0) {
      showBlockedModal({ name, locationCount })
      return
    }

    showConfirmModal({ uuid, name, hasInboxEmailAddress })
  }

  return {
    commands: {
      deactivate: deactivateLegalEntity,
    },
    states: {
      isLoading,
    },
  }
}
